import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2, RefreshCw, Plus, Edit2, Trash2, IdCard, Phone, Mail, MapPin, Cake, History, Tag } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import { CustomerHistoryDialog } from './CustomerHistoryDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyCustomer = {
  full_name: '',
  birth_date: '',
  dni: '',
  phone: '',
  email: '',
  address: '',
  is_minor: false,
  guardian_name: '',
  guardian_dni: '',
  guardian_phone: '',
  notes: '',
};

export const CustomerCardsTab = ({ authHeader, userRole }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // customer being edited, or {} for new
  const [submitting, setSubmitting] = useState(false);
  const [historyId, setHistoryId] = useState(null);

  const fetchCustomers = async () => {
    if (!authHeader) return;
    setLoading(true);
    try {
      const url = search.trim()
        ? `${API}/admin/customer-cards?q=${encodeURIComponent(search.trim())}`
        : `${API}/admin/customer-cards`;
      const res = await axios.get(url, { headers: { Authorization: authHeader } });
      setCustomers(res.data);
    } catch {
      toast.error('Error al cargar fichas de clientes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchCustomers, search ? 250 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeader, search]);

  const saveCustomer = async (formData) => {
    setSubmitting(true);
    try {
      if (editing?.id) {
        await axios.put(`${API}/admin/customer-cards/${editing.id}`, formData, {
          headers: { Authorization: authHeader },
        });
        toast.success('Ficha actualizada');
      } else {
        const res = await axios.post(`${API}/admin/customer-cards`, formData, {
          headers: { Authorization: authHeader },
        });
        if (res.data.existed) {
          toast.info(`Ya existía. Cliente ${res.data.customer_number}`);
        } else {
          toast.success(`Cliente creado: ${res.data.customer_number}`);
        }
      }
      setEditing(null);
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSubmitting(false);
    }
  };

  const deleteCustomer = async (c) => {
    if (!window.confirm(`Eliminar la ficha de ${c.full_name} (${c.customer_number})?`)) return;
    try {
      await axios.delete(`${API}/admin/customer-cards/${c.id}`, {
        headers: { Authorization: authHeader },
      });
      toast.success('Ficha eliminada');
      fetchCustomers();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h3 className="font-heading text-2xl text-halo-charcoal">Fichas de clientes</h3>
          <p className="text-sm text-halo-text">
            Cada cliente tiene un número único (HALO-CL-XXXXX) para identificarlo en futuras reservas.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Input
            placeholder="Buscar nº, nombre, DNI, teléfono..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-72"
            data-testid="customer-cards-search"
          />
          <Button
            onClick={() => setEditing({ ...emptyCustomer })}
            className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
            data-testid="new-customer-card"
          >
            <Plus size={16} className="mr-2" /> Nueva ficha
          </Button>
          <Button onClick={fetchCustomers} variant="outline" size="sm" disabled={loading}>
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {loading && (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-halo-brown" size={32} />
        </div>
      )}

      {!loading && customers.length === 0 && (
        <div className="bg-halo-surface rounded-xl border border-halo-taupe/10 p-10 text-center">
          <p className="text-halo-text">
            {search ? 'No se encontraron clientes con esa búsqueda.' : 'No hay clientes todavía.'}
          </p>
        </div>
      )}

      <div className="grid gap-3">
        {customers.map((c) => (
          <CustomerRow
            key={c.id}
            customer={c}
            userRole={userRole}
            onEdit={() => setEditing(c)}
            onDelete={() => deleteCustomer(c)}
            onHistory={() => setHistoryId(c.id)}
          />
        ))}
      </div>

      <CustomerDialog
        open={!!editing}
        customer={editing}
        onClose={() => setEditing(null)}
        onSave={saveCustomer}
        submitting={submitting}
      />

      <CustomerHistoryDialog
        open={!!historyId}
        customerId={historyId}
        authHeader={authHeader}
        onClose={() => setHistoryId(null)}
      />
    </div>
  );
};


const CustomerRow = ({ customer, userRole, onEdit, onDelete, onHistory }) => (
  <div
    className="bg-halo-surface rounded-xl border border-halo-taupe/10 p-5"
    data-testid={`customer-row-${customer.customer_number}`}
  >
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-mono font-bold text-halo-brown tracking-wider">
            {customer.customer_number}
          </span>
          {customer.is_minor && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700">
              Menor de edad
            </span>
          )}
        </div>
        <p className="text-halo-charcoal font-medium text-lg">{customer.full_name}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-2 text-sm text-halo-text">
          {customer.dni && (
            <p className="flex items-center gap-1">
              <IdCard size={14} /> {customer.dni}
            </p>
          )}
          {customer.phone && (
            <p className="flex items-center gap-1">
              <Phone size={14} /> {customer.phone}
            </p>
          )}
          {customer.email && (
            <p className="flex items-center gap-1 truncate">
              <Mail size={14} /> {customer.email}
            </p>
          )}
          {customer.birth_date && (
            <p className="flex items-center gap-1">
              <Cake size={14} /> {customer.birth_date}
            </p>
          )}
          {customer.address && (
            <p className="flex items-center gap-1 sm:col-span-2 truncate">
              <MapPin size={14} /> {customer.address}
            </p>
          )}
        </div>
        {customer.is_minor && customer.guardian_name && (
          <p className="text-xs text-halo-text mt-2 italic">
            Tutor: {customer.guardian_name} ({customer.guardian_dni || 's/DNI'}) · {customer.guardian_phone || ''}
          </p>
        )}
        {customer.acquisition_promo && (
          <p
            className="text-xs text-halo-brown mt-2 flex items-center gap-1"
            data-testid={`customer-acquisition-${customer.customer_number}`}
          >
            <Tag size={12} />
            Vino con código{' '}
            <span className="font-mono font-semibold">
              {customer.acquisition_promo}
            </span>
            {customer.acquisition_promo_at
              ? ` · ${customer.acquisition_promo_at.slice(0, 10)}`
              : ''}
          </p>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={onHistory}
          data-testid={`history-customer-${customer.customer_number}`}
        >
          <History size={14} className="mr-1" /> Historial
        </Button>
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-customer-${customer.customer_number}`}>
          <Edit2 size={14} />
        </Button>
        {userRole === 'admin' && (
          <Button
            size="sm"
            variant="outline"
            onClick={onDelete}
            className="text-red-500 border-red-200 hover:bg-red-50"
            data-testid={`delete-customer-${customer.customer_number}`}
          >
            <Trash2 size={14} />
          </Button>
        )}
      </div>
    </div>
  </div>
);


const CustomerDialog = ({ open, customer, onClose, onSave, submitting }) => {
  const [form, setForm] = useState(emptyCustomer);

  useEffect(() => {
    if (customer) {
      setForm({ ...emptyCustomer, ...customer });
    }
  }, [customer]);

  const submit = (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.phone.trim()) {
      toast.error('Nombre y teléfono son obligatorios');
      return;
    }
    // Strip empty optional fields so backend doesn't store ""
    const payload = { ...form };
    Object.keys(payload).forEach((k) => {
      if (payload[k] === '') delete payload[k];
    });
    onSave(payload);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {customer?.id ? `Editar ficha · ${customer.customer_number}` : 'Nueva ficha de cliente'}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-4">
          <div>
            <Label>Nombre y apellidos *</Label>
            <Input
              value={form.full_name}
              onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))}
              className="mt-1"
              required
              data-testid="customer-input-name"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Fecha de nacimiento</Label>
              <Input
                type="date"
                value={form.birth_date || ''}
                onChange={(e) => setForm((p) => ({ ...p, birth_date: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>DNI / NIE</Label>
              <Input
                value={form.dni || ''}
                onChange={(e) => setForm((p) => ({ ...p, dni: e.target.value }))}
                className="mt-1"
                placeholder="12345678A"
                data-testid="customer-input-dni"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Teléfono *</Label>
              <Input
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                className="mt-1"
                placeholder="612 345 678"
                required
                data-testid="customer-input-phone"
              />
            </div>
            <div>
              <Label>Correo electrónico</Label>
              <Input
                type="email"
                value={form.email || ''}
                onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                className="mt-1"
                data-testid="customer-input-email"
              />
            </div>
          </div>

          <div>
            <Label>Dirección</Label>
            <Input
              value={form.address || ''}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
              className="mt-1"
              placeholder="Calle, número, ciudad"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-halo-charcoal cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_minor}
              onChange={(e) => setForm((p) => ({ ...p, is_minor: e.target.checked }))}
              data-testid="customer-input-minor"
            />
            Es menor de edad (rellenar datos del tutor legal)
          </label>

          {form.is_minor && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
              <p className="text-xs text-amber-900">Datos del padre/madre/tutor legal</p>
              <div>
                <Label>Nombre del tutor</Label>
                <Input
                  value={form.guardian_name || ''}
                  onChange={(e) => setForm((p) => ({ ...p, guardian_name: e.target.value }))}
                  className="mt-1"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>DNI/NIE tutor</Label>
                  <Input
                    value={form.guardian_dni || ''}
                    onChange={(e) => setForm((p) => ({ ...p, guardian_dni: e.target.value }))}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Teléfono del tutor</Label>
                  <Input
                    value={form.guardian_phone || ''}
                    onChange={(e) => setForm((p) => ({ ...p, guardian_phone: e.target.value }))}
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          )}

          <div>
            <Label>Notas internas (opcional)</Label>
            <Textarea
              value={form.notes || ''}
              onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))}
              rows={2}
              className="mt-1"
              placeholder="Información adicional..."
            />
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-halo-brown hover:bg-halo-charcoal text-halo-cream"
              data-testid="customer-form-submit"
            >
              {submitting ? 'Guardando...' : (customer?.id ? 'Guardar cambios' : 'Crear ficha')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerCardsTab;
